import React, { useState } from "react";
import i18n from "../lib/i18n";
import { array, bool, number, object } from "prop-types";
import ChartSelector from "./ChartSelector";
import AnalysisPieChart from "./AnalysisPieChart";
import KPIBox from "./KPIBox";
import Tooltip from "./Tooltip";
import { classSet, currency, delimitedValue } from "../lib/helpers";
import {
  getColor,
  getChartColorsFromStatuses,
  preferredBudgetStatusColors,
} from "../lib/chartHelpers";
import TimeBarChart from "./TimeBarChart";
import UIBox from "./UIBox";
import colors from "@/stylesheets/colors.module.scss";
import { preferredEnergySavingsColors } from "../lib/chartHelpers";

const AnalysisPage = ({
  allowMultiplePlans,
  buildingShare,
  categoryShare,
  costPerYearAndArea,
  statusShare,
  facilityShare,
  yearlyAverageCost,
  yearlyCosts,
  yearlyNetCosts,
  yearlyCostsByActionType,
  yearlyEnergySavings,
  totalCost,
  maintenanceDebt,
  averageMaintenanceDebtPerArea,
  benchmarkMaintenanceDebtPerArea,
  investmentCost,
  maintenanceCost,
  totalEnergySavings,
  totalElectricitySavings,
  totalHeatingSavings,
  sustainabilityEnabled,
  budgetGraphEnabled,
  budgetIncludesVat,
  yearlyCostsByBudgetStatus,
  yearlyBudgetCaps,
}) => {
  const showBuldings = buildingShare.length > 1;

  const hasArea = costPerYearAndArea || averageMaintenanceDebtPerArea;
  const getTooltipText = (base) => {
    if (!hasArea) {
      return base;
    }

    const facilityString = allowMultiplePlans
      ? i18n.t("js.components.analysis_page.multiple_facilities")
      : i18n.t("js.components.analysis_page.one_facility");

    return i18n.t("js.components.analysis_page.tooltip_info", {
      base: base,
      facility_string: facilityString,
    });
  };

  const preferredActionTypeColors = {};
  preferredActionTypeColors[i18n.t("js.lib.chart_helpers.maintenance")] =
    getColor(0);
  preferredActionTypeColors[i18n.t("js.lib.chart_helpers.investment")] =
    getColor(1);

  // Pass colors used by category share pie chart to yearly chart
  // to synchronize the category colors between them
  const preferredCategoryColors = getPreferredColors(
    categoryShare,
    yearlyCosts
  );

  const [activeTab, setActiveTab] = useState("by_action_type");

  const tabs = [
    {
      key: "by_action_type",
      label: i18n.t("js.components.analysis_page.by_action_type"),
      component: (
        <div id="graph-yearly">
          <TimeBarChart
            series={yearlyCostsByActionType}
            lineData={sustainabilityEnabled ? yearlyNetCosts : undefined}
            preferredColors={preferredActionTypeColors}
          />
        </div>
      ),
    },
    {
      key: "by_category",
      label: i18n.t("js.components.analysis_page.by_category"),
      component: (
        <div id="graph-yearly">
          <TimeBarChart
            series={yearlyCosts}
            preferredColors={preferredCategoryColors}
          />
        </div>
      ),
    },
    ...(budgetGraphEnabled
      ? [
          {
            key: "by_budget",
            label: i18n.t(
              budgetIncludesVat
                ? "js.components.analysis_page.by_budget_including_vat"
                : "js.components.analysis_page.by_budget_excluding_vat"
            ),
            component: (
              <div id="graph-yearly">
                <TimeBarChart
                  series={yearlyCostsByBudgetStatus}
                  lineData={yearlyBudgetCaps}
                  preferredColors={preferredBudgetStatusColors(
                    yearlyCostsByBudgetStatus
                  )}
                />
              </div>
            ),
          },
        ]
      : []),
    ...(sustainabilityEnabled
      ? [
          {
            key: "by_savings",
            label: i18n.t("js.components.analysis_page.by_savings"),
            component: (
              <>
                <div id="graph-yearly">
                  <TimeBarChart
                    series={yearlyEnergySavings}
                    stacked={true}
                    reverseStackingOrder={true}
                    preferredColors={preferredEnergySavingsColors()}
                  />
                </div>
                <div className="info-block info-block--blue">
                  {i18n.t("js.components.analysis_page.previous_savings_info")}
                </div>
              </>
            ),
          },
        ]
      : []),
  ];

  const activeTabConfig = tabs.find((tab) => tab.key === activeTab);

  const graphHeader = () => {
    if (activeTab === "by_savings") {
      return i18n.t("js.components.analysis_page.graph_header_savings");
    }

    if (activeTab === "by_budget") {
      return activeTabConfig.label;
    }

    return i18n.t("js.components.analysis_page.graph_header");
  };

  const KPIBoxClassNames = classSet({
    "col-xs-12": true,
    "col-md-4": sustainabilityEnabled,
    "col-md-6": !sustainabilityEnabled,
    flex: true,
  });

  return (
    <>
      <UIBox>
        <ChartSelector
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          intercomTarget="analysis-chart-selector"
        />

        <h3 className="flex flex-jc-center">{graphHeader()}</h3>

        <div data-cy={`graph-actions-${activeTab}`}>
          {activeTabConfig.component}
        </div>
      </UIBox>

      <div className="row flex flex-flow-row-wrap">
        <div className={KPIBoxClassNames}>
          <KPIBox
            title={i18n.t("js.components.analysis_page.cost_kpi_title")}
            tooltip={getTooltipText(
              i18n.t("js.components.analysis_page.cost_kpi_tooltip")
            )}
            extras={[
              <div data-cy="annual-cost">
                <span className="puts-text puts-text--label-small-semi-bold">
                  {currency(yearlyAverageCost, true)}
                </span>{" "}
                {i18n.t("js.components.analysis_page.per_year")}
              </div>,
              costPerYearAndArea && (
                <div data-cy="cost-per-area">
                  <span className="puts-text puts-text--label-small-semi-bold">
                    {currency(costPerYearAndArea, false)}
                  </span>{" "}
                  {i18n.t("js.components.analysis_page.per_year_and_area")}
                </div>
              ),
            ]}
          >
            <div className="flex flex-column gap-16">
              <div className="c-stats-value">
                <div className="c-stats-value__content" data-cy="total-cost">
                  <span className="c-stats-value__value">
                    {delimitedValue(totalCost, true)}
                  </span>{" "}
                  <span className="c-stats-value__unit">
                    {i18n.t("js.helpers.currency")}
                  </span>
                </div>
              </div>

              <div className="flex flex-column gap-8">
                <div
                  className="flex align-items-center gap-8"
                  data-cy="investment-cost"
                >
                  <span className="label label-light-green min-w-80">
                    {i18n.t("js.components.analysis_page.investment")}
                  </span>
                  <span className="puts-text puts-text--label-small-semi-bold">
                    {currency(investmentCost, true)}
                  </span>
                </div>
                <div
                  className="flex align-items-center gap-8"
                  data-cy="maintenance-cost"
                >
                  <span className="label label-light-orange min-w-80">
                    {i18n.t("js.components.analysis_page.maintenance")}
                  </span>
                  <span className="puts-text puts-text--label-small-semi-bold">
                    {currency(maintenanceCost, true)}
                  </span>
                </div>
              </div>
            </div>
          </KPIBox>
        </div>

        <div className={KPIBoxClassNames}>
          <KPIBox
            title={i18n.t("js.components.analysis_page.maintenance_debt")}
            tooltip={getTooltipText(
              i18n.t("js.components.analysis_page.maintenance_debt_tooltip")
            )}
            extras={
              hasArea
                ? [
                    <div data-cy="maintenance-debt-per-area">
                      <MaintenanceDebtBenchmarkIndicator
                        current={averageMaintenanceDebtPerArea}
                        benchmark={benchmarkMaintenanceDebtPerArea}
                      />
                      <span className="puts-text puts-text--label-small-semi-bold">
                        {currency(averageMaintenanceDebtPerArea, false)}
                      </span>{" "}
                      {i18n.t("js.components.analysis_page.per_area")}
                    </div>,
                  ]
                : []
            }
          >
            <div className="c-stats-value">
              <div
                className="c-stats-value__content"
                data-cy="maintenance-debt"
              >
                <span className="c-stats-value__value">
                  {delimitedValue(maintenanceDebt, true)}
                </span>{" "}
                <span className="c-stats-value__unit">
                  {i18n.t("js.helpers.currency")}
                </span>
              </div>
            </div>
          </KPIBox>
        </div>

        {sustainabilityEnabled && (
          <div className={KPIBoxClassNames}>
            <KPIBox
              title={i18n.t("js.components.analysis_page.total_energy_savings")}
              tooltip={i18n.t(
                "js.components.analysis_page.previous_savings_info"
              )}
              extras={[
                <>
                  <span>
                    {i18n.t(
                      "js.components.analysis_page.total_electricity_savings"
                    )}
                    :{" "}
                  </span>
                  <span
                    className="puts-text puts-text--label-small-semi-bold"
                    data-cy="total-electricity-savings"
                  >
                    {currency(totalElectricitySavings, true)}
                  </span>
                </>,
                <>
                  <span>
                    {i18n.t(
                      "js.components.analysis_page.total_heating_savings"
                    )}
                    :{" "}
                  </span>
                  <span
                    className="puts-text puts-text--label-small-semi-bold"
                    data-cy="total-heating-savings"
                  >
                    {currency(totalHeatingSavings, true)}
                  </span>
                </>,
              ]}
            >
              <div className="c-stats-value c-stats-value--energy-saving">
                <div
                  className="c-stats-value__content"
                  data-cy="total-energy-savings"
                >
                  <span className="c-stats-value__value">
                    {delimitedValue(totalEnergySavings, true)}
                  </span>{" "}
                  <span className="c-stats-value__unit">
                    {i18n.t("js.helpers.currency")}
                  </span>
                </div>
              </div>
            </KPIBox>
          </div>
        )}
      </div>

      <ChartRow
        {...{
          facilityShare,
          categoryShare,
          buildingShare,
          statusShare,
          showBuldings,
          allowMultiplePlans,
        }}
      />
    </>
  );
};

const ChartRow = ({
  facilityShare,
  categoryShare,
  buildingShare,
  statusShare,
  showBuldings,
  allowMultiplePlans,
}) => {
  const charts = [
    {
      key: "facility",
      series: facilityShare,
      name: i18n.t("js.components.analysis_page.facility"),
      display: allowMultiplePlans,
    },
    {
      key: "category",
      series: categoryShare,
      name: i18n.t("js.components.analysis_page.category"),
      display: true,
    },
    {
      key: "building",
      series: buildingShare,
      name: i18n.t("js.components.analysis_page.building"),
      display: showBuldings && !allowMultiplePlans,
    },
    {
      key: "status",
      series: statusShare,
      name: i18n.t("js.components.analysis_page.status"),
      display: true,
      colors: getChartColorsFromStatuses(statusShare.map((item) => item[0])),
    },
  ].filter((chart) => chart.display);

  return (
    <div className="row">
      {charts.map((chart) => (
        <AnalysisPieChart
          series={chart.series}
          name={chart.name}
          cols={12 / charts.length}
          colors={chart.colors}
          key={chart.key}
        />
      ))}
    </div>
  );
};

const MaintenanceDebtBenchmarkIndicator = ({ current, benchmark }) => {
  let baseTooltip = i18n.t(
    "js.components.analysis_page.benchmark_base_tooltip",
    {
      benchmark: currency(benchmark, false),
    }
  );
  const ratio = Math.round((100 * current) / benchmark) / 100;
  let comparisonTooltip = null;
  if (ratio > 1) {
    comparisonTooltip = i18n.t("js.components.analysis_page.higher_value", {
      percentage: Math.round((ratio - 1) * 100),
    });
  } else if (ratio < 1) {
    comparisonTooltip = i18n.t("js.components.analysis_page.lower_value", {
      percentage: Math.round((1 - ratio) * 100),
    });
  }

  let color = colors.yellow;
  if (current > benchmark * 1.2) {
    color = colors.red;
  } else if (current < benchmark * 0.8) {
    color = colors.green;
  }

  const tooltip = `${baseTooltip} ${comparisonTooltip || ""}`.trim();
  return (
    <Tooltip
      icon="fa-circle"
      text={tooltip}
      style={{ color, margin: 0, marginRight: 5 }}
    />
  );
};

const getPreferredColors = (categoryShare, yearlyCosts) => {
  const pieChartCategories = categoryShare
    .map((item) => item[0])
    .filter((item) => item !== i18n.t("js.components.analysis_page.misc"));
  const barChartCategories = yearlyCosts.map((item) => item.label);
  const uniqueCategories = [
    ...new Set(pieChartCategories.concat(barChartCategories)),
  ];

  let preferredColors = {};
  uniqueCategories.forEach((label, index) => {
    preferredColors[label] = getColor(index);
  });

  return preferredColors;
};

export default AnalysisPage;

AnalysisPage.propTypes = {
  allowMultiplePlans: bool.isRequired,
  buildingShare: array.isRequired,
  categoryShare: array.isRequired,
  costPerYearAndArea: number,
  statusShare: array.isRequired,
  yearlyAverageCost: number.isRequired,
  yearlyCosts: array.isRequired,
  sustainabilityEnabled: bool.isRequired,
  budgetGraphEnabled: bool.isRequired,
  budgetIncludesVat: bool.isRequired,
  yearlyCostsByBudgetStatus: array.isRequired,
  yearlyBudgetCaps: object,
};
